import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import get from 'lodash/get';
import { type ChangeEvent, type ComponentProps, type ReactElement, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormFeedback, FormGroup, Input, InputGroup, InputGroupText, Label } from 'reactstrap';

// Import the two icons directly, not through the `src/assets/icons` barrel: the
// barrel statically pulls in the ~27 kB BasicNotificationSvg module, and
// InputField ships in most lazy modal chunks, so webpack ended up copying that
// module into each of them (APX-827: +~33 kB gzip total client JS).
import PasswordHide from 'src/assets/icons/PasswordHide';
import PasswordShow from 'src/assets/icons/PasswordShow';
import { checkVendorDomain } from 'src/shared/utils/hosts';

export interface InputFieldProps {
  name: string;
  type: ComponentProps<typeof Input>['type'];
  placeholder?: string;
  defaultValue?: string;
  hideFormError?: boolean;
  id?: string;
  value?: string;
  className?: string;
  checked?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  label?: string;
  labelClass?: string;
  size?: string;
  inputGroupItems?: React.ReactNode;
  formGroupClass?: string;
  onClick?: () => void;
  mandatory?: boolean;
  readOnly?: boolean;
  customHandleChange?: (inputEvent: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  rows?: number;
  // react-tooltip anchor on the form group rather than the control itself, so the
  // tooltip still opens when the control is disabled and swallows pointer events.
  tooltipId?: string;
  tooltipContent?: string;
  /** For a control the design gives no visible label — a time field in a from/to row. */
  'aria-label'?: string;
  'data-testid'?: string;
}

interface RenderInputType {
  inputGroupItems: React.ReactNode;
  setIsPasswordVisible: (value: boolean) => void;
  isPasswordVisible: boolean;
  type: ComponentProps<typeof Input>['type'];
  children: ReactElement<Input>;
  errorMessageComponent: () => React.ReactNode;
}

const RenderInput: React.FC<RenderInputType> = (props) => {
  const {
    inputGroupItems,
    setIsPasswordVisible,
    isPasswordVisible,
    type,
    children,
    errorMessageComponent,
  } = props;
  if (inputGroupItems || type === 'password') {
    return (
      <InputGroup className="ls-input-group">
        {children}
        <InputGroupText onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
          {inputGroupItems}
          {type === 'password' ? (
            <i>{isPasswordVisible ? <PasswordShow /> : <PasswordHide />}</i>
          ) : (
            ''
          )}
        </InputGroupText>
        {errorMessageComponent()}
      </InputGroup>
    );
  }
  if (children) {
    return (
      <>
        {children}
        {errorMessageComponent()}
      </>
    );
  }
  return <div />;
};

const InputField: React.FC<InputFieldProps> = (props) => {
  const isVendor = checkVendorDomain();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    name,
    type,
    placeholder = '',
    hideFormError = false,
    value,
    checked = false,
    label,
    labelClass = '',
    inputGroupItems,
    formGroupClass = '',
    mandatory = false,
    size = '',
    className = '',
    readOnly = false,
    customHandleChange,
    tooltipId,
    tooltipContent,
    'data-testid': testId,
    ...inputProps
  } = props;

  const errorMessageComponent = () => {
    if (!hideFormError) {
      return (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <FormFeedback data-testid={`${name}-error`}>{message}</FormFeedback>
          )}
        />
      );
    }
    return '';
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormGroup
          className={classNames(
            'ls-form-group',
            { 'wo-jc': !hideFormError && isVendor },
            size,
            formGroupClass,
          )}
          data-testid={`${name}-input-field`}
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltipContent}
        >
          {label ? (
            <Label for="" className={labelClass} data-testid={`${name}-label`}>
              {label} {mandatory ? <span>*</span> : ''}
            </Label>
          ) : (
            ''
          )}
          <RenderInput
            inputGroupItems={inputGroupItems}
            setIsPasswordVisible={setIsPasswordVisible}
            isPasswordVisible={isPasswordVisible}
            type={type}
            errorMessageComponent={errorMessageComponent}
          >
            <Input
              {...field}
              type={type === 'password' && isPasswordVisible ? 'text' : type}
              placeholder={placeholder}
              value={value || field?.value}
              checked={checked}
              {...inputProps}
              className={`${className ?? ''} ${
                !inputGroupItems || type === 'password' ? size : ''
              }`}
              onChange={(inputChangeEvent) => {
                if (customHandleChange) {
                  field.onChange(customHandleChange(inputChangeEvent));
                } else {
                  field.onChange(inputChangeEvent);
                }
              }}
              invalid={hideFormError ? false : !!get(errors, name)}
              readOnly={readOnly || false}
              data-testid={testId ?? `${name}-input`}
            />
          </RenderInput>
        </FormGroup>
      )}
    />
  );
};

export default InputField;
